<template>
    <div>
        <report-header
            :title="title"
            :isShowDateRange="true"
            :formatDateRangeTitle="dateRange"
        />

        <b-card>
            <div id="report-body" class="w-100">
                <QTable
                    :columns="columns"
                    :data="tableData"
                    hide-bottom
                    :loading="isLoading"
                    class="mb-4"
                    :rows-per-page-options="[0]"
                    :row-key="row => row.reminderId + row.reminderActorId"
                >
                    <template
                        v-slot:bottom-row
                        v-if="tableData.length == 0 && !isLoading"
                    >
                        <q-tr>
                            <q-td colspan="12">No records available</q-td>
                        </q-tr>
                    </template>
                    <template v-slot:header-cell-checkbox>
                        <th style="vertical-align: middle;">
                            <input
                                type="checkbox"
                                style="cursor: pointer;"
                                v-model="checkAll"
                            />
                        </th>
                    </template>
                    <template v-slot:body-cell-checkbox="props">
                        <td>
                            <input
                                type="checkbox"
                                style="cursor: pointer;"
                                v-model="checked"
                                :value="generateCheckboxValue(props.row)"
                            />
                        </td>
                    </template>
                    <template v-slot:body-cell-due="props">
                        <td>
                            {{ props.value }}
                        </td>
                    </template>
                    <template v-slot:body-cell-owner="props">
                        <td>
                            <router-link
                                v-if="props.row.clientId"
                                :to="`/client/profile/${props.row.clientEncodedId}`"
                            >
                                {{ props.value }}
                            </router-link>
                        </td>
                    </template>
                    <template v-slot:body-cell-patientName="props">
                        <td>
                            <router-link
                                v-if="props.row.patientId"
                                :to="`/patient/profile/${props.row.patientEncodedId}`"
                            >
                                {{ props.value }}
                            </router-link>
                        </td>
                    </template>
                    <template v-slot:body-cell-itemName="props">
                        <td>
                            <span
                                v-if="
                                    isLoadingEditReminderId ===
                                    props.row.reminderId + props.row.id
                                "
                            >
                                <spinner />
                            </span>
                            <a
                                v-else
                                href="#"
                                @click.prevent="openModal(props.row)"
                            >
                                {{ props.value }}
                            </a>
                        </td>
                    </template>
                    <template v-slot:body-cell-lastSent="props">
                        <td>
                            {{ props.value }}
                        </td>
                    </template>
                    <template v-slot:body-cell-nextVisit="props">
                        <td>
                            {{ props.value }}
                        </td>
                    </template>
                </QTable>
            </div>
        </b-card>

        <ModalReminder
            ref="modalEditReminder"
            :reminder="selectedReminder"
            :clientId="selectedClientId"
            @reload="fetchData(currentFetchParams)"
        />
    </div>
</template>

<script>
import { QTable } from "quasar";
import "@/css/quasar-table.css";
import Report from "@/mixins/report.js";
import ModalReminder from "@/components/client-reminders/reminder-save-modal";
import Spinner from "@/components/elements/spinner";
import CREPORT from "@/constants/report.js";

const STORAGE_KEY = "reminders_detail_checked";
const PATIENT_STATUS_ACTIVE = 1;
const PATIENT_STATUS_INACTIVE = 2;
const PATIENT_STATUS_DECEASED = 3;

export default {
    components: {
        QTable,
        ModalReminder,
        Spinner,
        ReportHeader: () => import("@/components/report/report-header")
    },
    mixins: [Report],
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: this.getStartDate("week"),
                    end: this.getEndDate("day")
                };
            }
        }
    },
    watch: {
        checkAll(value) {
            this.toggleAll(value);
        },
        checked(items = []) {
            this.generateActionData(items);
        }
    },
    data() {
        return {
            title: "Reminders Detail",
            columns: [
                {
                    name: "checkbox",
                    field: "checkbox",
                    label: "",
                    headerStyle: "width:50px"
                },
                {
                    name: "due",
                    field: "due",
                    label: "Due",
                    align: "left",
                    headerStyle: "width:130px; font-weight:bold;"
                },
                {
                    name: "reminderType",
                    field: "reminderType",
                    label: "Type",
                    align: "left",
                    headerStyle: "width:200px; font-weight:bold;"
                },
                {
                    name: "owner",
                    field: "owner",
                    label: "Owner",
                    align: "left",
                    headerStyle: "font-weight:bold;"
                },
                {
                    name: "patientName",
                    field: "patientName",
                    label: "Patient",
                    align: "left",
                    headerStyle: "font-weight:bold;"
                },
                {
                    name: "itemName",
                    field: "itemName",
                    label: "Reminder For",
                    align: "left",
                    headerStyle: "font-weight:bold;"
                },
                {
                    name: "note",
                    field: "note",
                    label: "Notes",
                    align: "left",
                    headerStyle: "font-weight:bold;"
                },
                {
                    name: "lastSent",
                    field: "lastSent",
                    label: "Last Sent",
                    align: "left",
                    headerStyle: "font-weight:bold;"
                },
                {
                    name: "nextVisit",
                    field: "nextVisit",
                    label: "Client Next Visit",
                    align: "left",
                    headerStyle: "font-weight:bold;"
                },
                {
                    name: "homeNumber",
                    field: "homeNumber",
                    label: "Home",
                    align: "left",
                    headerStyle: "font-weight:bold;"
                },
                {
                    name: "mobileNumber",
                    field: "mobileNumber",
                    label: "Mobile",
                    align: "left",
                    headerStyle: "font-weight:bold;"
                },
                {
                    name: "email",
                    field: "email",
                    label: "Email",
                    align: "left",
                    headerStyle: "font-weight:bold;"
                }
            ],
            tableData: [],
            isLoading: true,
            selectedReminder: null,
            selectedClientId: null,
            checked: [],
            checkAll: false,
            isLoadingEditReminderId: null,
            currentFetchParams: null,
            dateRange: ""
        };
    },
    computed: {
        periodStart() {
            if (this.data.start) {
                return this.data.start.format(this.getDateFormat());
            }
            return null;
        },
        periodEnd() {
            if (this.data.end) {
                return this.data.end.format(this.getDateFormat());
            }
            return null;
        }
    },
    async mounted() {
        let self = this;
        // --- toggle navigation options
        self.data.isShowDateRange = true;
        self.data.exportOption = false; // by default export is hidden

        await self._getSetting();
        self.data.patientStatus = [
            {
                value: PATIENT_STATUS_ACTIVE,
                label: "Active"
            },
            { value: PATIENT_STATUS_INACTIVE, label: "Inactive" }
        ];
        self.data.exportOption = this._hasAccess("report.export");
        self.data.start = self.getStartDate("week");
        self.data.end = self.getEndDate("day");
        self.data.from = self.converToUnix(self.data.start);
        self.data.to = self.converToUnix(self.data.end);
        self.fetchData();

        // filter dialog
        self.data.filterTitle = "Filter Reminders";
        self.data.isShowRemindersCategory = true;
        self.data.isShowRemindersTypes = true;
        self.data.isShowNextVisit = true;
        self.data.isShowPatientStatus = true;
        self.data.reportId = CREPORT.TYPE.REMINDERS_DETAIL;

        // hide refresh button
        self.data.refreshOption = false;

        self.$root.$on("report-filter-date-range:refresh", self.fetchData);

        window.scrollTo(0, 0);
        sessionStorage.removeItem(STORAGE_KEY);
    },
    beforeDestroy() {
        this.resetFilterModal();
        sessionStorage.removeItem(STORAGE_KEY);
        this.$root.$off("report-filter-date-range:refresh", this.fetchData);
    },
    methods: {
        resetCheckBoxes() {
            this.checkAll = false;
            this.checked = [];
            sessionStorage.removeItem(STORAGE_KEY);
        },
        generateActionData(items = []) {
            let raw = JSON.stringify(items);
            sessionStorage.setItem(STORAGE_KEY, raw);
        },
        toggleAll(isChecked) {
            if (isChecked) {
                let arr = [];

                for (let record of this.tableData) {
                    let value = this.generateCheckboxValue(record);
                    arr.push(value);
                }

                this.checked = arr;
            } else {
                this.checked = [];
            }
        },
        generateCheckboxValue(obj) {
            // format
            // eg: `f0ec9d18-7b6f-49,b0efce59-aa37-4d,5,b85a0dda-ee63-47`
            let arr = [
                obj.reminderId,
                obj.reminderActorId,
                obj.reminderTypeId,
                obj.clientId
            ];

            if (obj.patientId) {
                arr.push(obj.patientId);
            }

            return arr.join(",");
        },
        async openModal(reminder) {
            this.isLoadingEditReminderId = reminder.reminderId + reminder.id;

            let reminderInfo = await this.$http.get(
                `${process.env.BARRAMUNDI}schedule/reminder/${reminder.reminderId}`
            );

            this.isLoadingEditReminderId = null;

            if (
                reminderInfo.data.response &&
                reminderInfo.data.response.resources
            ) {
                this.selectedReminder = {
                    ...reminderInfo.data.response.resources,
                    client_id: reminder.clientId,
                    client_name: reminder.owner
                };

                this.selectedClientId = reminder.clientId;
                this.$refs.modalEditReminder.open();
            } else {
                this.selectedReminder = null;
            }
        },
        resetFilterModal() {
            let self = this;
            self.data.reportId = null;
            self.data.filterTitle = "Filter Date";
            self.data.isShowRemindersCategory = false;
            self.data.isShowRemindersTypes = false;
            self.data.isShowNextVisit = false;
            self.data.isShowPatientStatus = false;
        },
        async fetchData(params) {
            let fetchParams;

            if (params) {
                this.currentFetchParams = params; // store if any

                fetchParams = {
                    from: params.from,
                    to: params.to,
                    next_visit: params.remindersNextVisit
                };

                if (params.remindersType) {
                    fetchParams["type"] = params.remindersType.map(
                        type => type.value
                    );
                }

                if (params.remindersCategory) {
                    fetchParams["category"] = params.remindersCategory.map(
                        cat => cat.id
                    );
                }

                if (params.patientStatus) {
                    fetchParams["patient_status"] = params.patientStatus.map(
                        status => status.value
                    );
                }
            } else {
                fetchParams = {
                    from: this.data.from,
                    to: this.data.to,
                    next_visit: 1,
                    patient_status: [
                        PATIENT_STATUS_ACTIVE,
                        PATIENT_STATUS_INACTIVE
                    ] // by default return status active and inactive
                };
            }

            this.isLoading = true;
            sessionStorage.removeItem(STORAGE_KEY);

            let res = await this.$http.get(
                `${process.env.BARRAMUNDI}report/reports/` +
                    CREPORT.TYPE.REMINDERS_DETAIL,
                {
                    params: fetchParams
                }
            );

            this.isLoading = false;
            let response = res.data.response;
            this.dateRange = this.getDateRange(this.data.from, this.data.to);

            if (response.message && response.message.code === 200) {
                this.tableData = response.resource.results;
                this.dateRange = this.getDateRange(
                    response.resource.fromDate,
                    response.resource.toDate
                );
                this.dynamicPageTitle(response.resource.meta.title);
            } else {
                this.tableData = [];
            }
        }
    }
};
</script>

<style scoped>
.report-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 5px;
}
.report-heading .period {
    font-size: 0.7em;
}
th {
    font-weight: bold;
    white-space: nowrap;
}
.card-body {
    padding-top: 0;
    padding-bottom: 0;
}
.report-description {
    display: block;
    margin-bottom: 10px;
    font-style: italic;
}
.total {
    font-weight: bold;
    border-top: 3px double #e4e4e4;
}
</style>
